import deepEqual from "deep-equal";
import { put, call, takeLatest, all, select } from "redux-saga/effects";

import { searchRequest } from "@/shared/api/search/SearchAPI";
import { searchActionConstants, searchActions, systemActions } from "../actions";
import { formatSearchParams } from "@/components/SearchWidget/SearchWidget.utils";
import {
  LOADER_ID_DICTIONARY_LOAD,
  LOADER_ID_LOAD_SEARCH,
} from "@/shared/store/search/constants";
import { selectorSearchParams } from "@/shared/store/search/selectors";
import {
  fetchFakeRelatedSearchSuggestions,
} from "@/shared/api/suggestions/suggestions";
import {
  searchDictionaryContent,
} from "@/shared/api/search/advanced/requests";
import { dictionaryAllFilterKey, SearchType } from "@/shared/utils/search/search.constants";
import { _sharedWorkerFetchSuggestionsPopup } from "@/shared/store/search/saga";
import { consoleLogError } from "@/shared/utils/console";
import { actionsFeaturedSearch } from "@/store/actions/featuredSearch.actions";
import { validateSearchQuery } from "@/shared/utils/search/search";

const TAG = "searchSaga_";

function* fetchSearchMethod(action) {
  const { actionSearchParams, onSuccess } = action.data;
  const searchParams = yield select(selectorSearchParams);
  const actionSearchParamsFormatted = formatSearchParams(actionSearchParams);

  actionSearchParamsFormatted.timestamp = Date.now();

  const { extras, query, lang, context, start, limit } = actionSearchParamsFormatted;
  const { type } = extras;
  if (type === SearchType.related && context === "suggestions" && lang) {
    const resultsSuggestions = yield call(fetchFakeRelatedSearchSuggestions, query, lang);
    yield put(
      searchActions.searchSuccess({
        searchParams: actionSearchParamsFormatted,
        search: {
          total: resultsSuggestions.length,
          results: resultsSuggestions.splice(start, limit),
        },
      }),
    );
    return;
  }

  if (!deepEqual(actionSearchParamsFormatted, searchParams)) {
    if (!validateSearchQuery(actionSearchParamsFormatted.query)) {
      return;
    }

    const notFound = { total: 0, results: [] };
    yield put(systemActions.loading(LOADER_ID_LOAD_SEARCH));
    const search = yield call(searchRequest, actionSearchParamsFormatted);

    const result = search || notFound;

    if (onSuccess) {
      onSuccess(result);
    }
    yield put(
      searchActions.searchSuccess({
        search: result,
        searchParams: actionSearchParamsFormatted,
      }),
    );
    yield put(systemActions.loading(LOADER_ID_LOAD_SEARCH, true));

    yield put(
      actionsFeaturedSearch.fetchByQuery(
        actionSearchParamsFormatted.query, searchParams.folders
      ),
    );
  }
}

function* fetchSearchDictionaryWorker(action) {
  try {
    let result;
    const { query, dictionary } = action.data;

    yield put(systemActions.loading(LOADER_ID_DICTIONARY_LOAD));

    if (dictionary === dictionaryAllFilterKey) {
      result = yield call(searchDictionaryContent, query, null);
    } else {
      result = yield call(searchDictionaryContent, query, dictionary);
    }

    if (Array.isArray(result)) {
      yield put(searchActions.searchDictionarySuccess({ query, dictionary }, result));
    } else {
      yield put(searchActions.searchDictionarySuccess({ query, dictionary }, []));
    }
  } catch (error) {
    consoleLogError(TAG, "fetchSearchDictionaryWorker ERROR: ", {
      error, action,
    });
  } finally {
    yield put(systemActions.loading(LOADER_ID_DICTIONARY_LOAD, true));
  }
}

export default function* searchSaga() {
  yield all([
    takeLatest(searchActionConstants.ACTION_SEARCH, fetchSearchMethod),
    takeLatest(searchActionConstants.FETCH_SUGGESTION, _sharedWorkerFetchSuggestionsPopup),
    takeLatest(searchActionConstants.FETCH_SEARCH_DICTIONARY, fetchSearchDictionaryWorker),
  ]);
}
